import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, XCircle } from "lucide-react";

const SUB_STATUS = {
  active: { label: "Active", cls: "bg-emerald-400/10 text-emerald-300 border-emerald-400/40" },
  cancelled: { label: "Cancelled", cls: "bg-red-400/10 text-red-300 border-red-400/40" },
};
const FILTERS = ["all", "active", "cancelled"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(iso || "").slice(0, 10);
  }
};

export const AdminSubscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [cancellingId, setCancellingId] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/admin/subscriptions${filter !== "all" ? `?status=${filter}` : ""}`)
      .then((r) => setSubs(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (sub) => {
    if (!window.confirm(`Cancel the monthly subscription for ${sub.email}?`)) return;
    setCancellingId(sub.id);
    try {
      await api.post(`/admin/subscriptions/${sub.id}/cancel`);
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? { ...s, status: "cancelled" } : s)));
      toast.success("Subscription cancelled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancellingId("");
    }
  };

  return (
    <div data-testid="admin-subscriptions">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-sub-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors duration-200 sq-md sq-press ${
              filter === f ? "bg-electric text-black border-electric" : "bg-transparent text-white border-white/25 hover:border-electric hover:text-electric"
            }`}
            data-testid={`sub-filter-${f}`}
          >
            {f === "all" ? "All" : SUB_STATUS[f].label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="py-20 text-center text-sm tracking-widest uppercase text-white/60">Loading...</p>
      ) : subs.length === 0 ? (
        <p className="py-20 text-center text-sm tracking-[0.2em] uppercase text-white/40" data-testid="admin-subs-empty">No subscriptions found</p>
      ) : (
        <div className="border border-white/15 sq-lg overflow-hidden" data-testid="admin-subs-table">
          <div className="hidden md:grid grid-cols-[1fr_110px_120px_90px_120px_120px] border-b border-white/15 bg-electric text-black text-[10px] tracking-[0.2em] uppercase font-bold">
            <div className="p-3">Customer</div>
            <div className="p-3">Started</div>
            <div className="p-3">Next Reorder</div>
            <div className="p-3">Items</div>
            <div className="p-3">Status</div>
            <div className="p-3 text-right">Action</div>
          </div>
          {subs.map((s) => {
            const meta = SUB_STATUS[s.status] || SUB_STATUS.active;
            return (
              <div
                key={s.id}
                className="grid grid-cols-2 md:grid-cols-[1fr_110px_120px_90px_120px_120px] items-center border-b border-white/12 last:border-b-0"
                data-testid={`admin-sub-row-${s.id}`}
              >
                <div className="p-3 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{s.email}</div>
                  <div className="text-[10px] text-white/40 truncate">First order {s.first_order_number || ", "}</div>
                </div>
                <div className="p-3 text-xs text-white/60">{fmtDate(s.created_at)}</div>
                <div className="p-3 text-xs text-white/60">{s.status === "active" ? fmtDate(s.next_reorder_at) : ", "}</div>
                <div className="p-3 text-xs text-white/70">{(s.items || []).length}</div>
                <div className="p-3">
                  <span className={`inline-block text-[10px] tracking-[0.12em] uppercase font-bold border px-2.5 py-1 sq-sm ${meta.cls}`} data-testid={`sub-status-${s.id}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="p-3 md:text-right">
                  {s.status === "active" ? (
                    <button
                      onClick={() => cancel(s)}
                      disabled={cancellingId === s.id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.15em] uppercase font-bold border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors duration-200 sq-sm sq-press disabled:opacity-50"
                      data-testid={`sub-cancel-${s.id}`}
                    >
                      {cancellingId === s.id ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />} Cancel
                    </button>
                  ) : (
                    <span className="text-[10px] tracking-[0.15em] uppercase text-white/30">, </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
